import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Wrench, Mail, Phone } from 'lucide-react';
import api from '../../lib/api';

const formatDateLong = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
};

const MaintenanceSplash = ({ children }) => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/settings/site-info')
      .then((r) => setInfo(r.data))
      .catch(() => setInfo(null))
      .finally(() => setLoading(false));
  }, []);

  // While loading, render children to avoid blocking flash
  if (loading) return children;
  if (!info?.maintenance_mode) return children;

  const message = info.maintenance_message || 'Le site est en maintenance. Nous serons de retour très bientôt.';
  const until = info.maintenance_until ? formatDateLong(info.maintenance_until) : '';
  const email = info.email || 'contact@theenglishspeakingcenter.fr';
  const phone = info.phone || '';

  return (
    <div
      className="min-h-screen relative overflow-hidden flex items-center justify-center px-4 py-16"
      style={{
        background: 'radial-gradient(circle at top left, rgba(40,91,187,0.18), transparent 50%), radial-gradient(circle at bottom right, rgba(255,58,45,0.12), transparent 50%), linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #2C3C65 100%)',
      }}
      data-testid="maintenance-splash"
    >
      {/* Decorative grain */}
      <div className="absolute inset-0 opacity-10 pointer-events-none mix-blend-overlay"
           style={{ backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22/></filter><rect width=%22200%22 height=%22200%22 filter=%22url(%23n)%22 opacity=%22.6%22/></svg>")' }} />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative max-w-2xl w-full text-center"
      >
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-8 rounded-full bg-amber-400/10 border border-amber-300/30 backdrop-blur-sm">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-300 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400" />
          </span>
          <span className="text-amber-200 text-xs uppercase tracking-widest font-semibold">Site en maintenance</span>
        </div>

        {/* Tool icon */}
        <motion.div
          animate={{ rotate: [0, -10, 10, -8, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
          className="inline-flex w-20 h-20 mb-6 rounded-2xl bg-gradient-to-br from-amber-300 to-amber-500 items-center justify-center shadow-2xl shadow-amber-500/30"
        >
          <Wrench className="h-10 w-10 text-slate-900" strokeWidth={2.5} />
        </motion.div>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
          On revient<br />
          <span className="bg-gradient-to-r from-amber-300 via-amber-200 to-rose-300 bg-clip-text text-transparent">très bientôt.</span>
        </h1>

        <p className="text-lg text-slate-300 max-w-xl mx-auto mb-8">
          {message}
        </p>

        {until && (
          <div className="inline-block px-5 py-3 mb-10 bg-white/5 border border-white/10 backdrop-blur rounded-xl">
            <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Retour prévu</p>
            <p className="text-lg font-semibold text-white capitalize">{until}</p>
          </div>
        )}

        {/* Contact card */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm">
          <p className="text-sm text-slate-400 mb-4">En cas d'urgence, vous pouvez nous joindre :</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={`mailto:${email}`}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-white text-slate-900 font-semibold rounded-xl hover:bg-amber-100 transition-colors"
            >
              <Mail className="h-4 w-4" />
              {email}
            </a>
            {phone && (
              <a
                href={`tel:${phone.replace(/\s/g, '')}`}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-white/20 text-white font-semibold rounded-xl hover:bg-white/10 transition-colors"
              >
                <Phone className="h-4 w-4" />
                {phone}
              </a>
            )}
          </div>
        </div>

        <p className="text-xs text-slate-500 mt-12">
          The English Speaking Center · Istres
        </p>
      </motion.div>
    </div>
  );
};

export default MaintenanceSplash;
